/**
 * Invoicing Endpoints
 */
import { getApiClient } from "../client";
import { PAYMENT_METHOD_POPULATE } from "../populate";
import { BillingDataSchema, ClaimEligibilitySchema, ClaimHistoryResponseSchema, ClaimPreviewSchema, CryptoPaymentConfigSchema, CryptoPaymentOptionsSchema, InvoiceSchema, ManualPaymentHistoryItemSchema, PaymentEligibilityResultSchema, PaymentMethodSchema, ReferrerClassificationSchema, RevolutPaymentConfirmationSchema, RevolutPaymentMethodTokenSchema, TaxProfileValidationResultSchema, TaxReportSchema, ViesVerificationResultSchema, } from "../schemas";
import { parseStrapiItemResponse, parseStrapiListResponse } from "../utils/parseStrapiResponse";
/**
 * Invoicing endpoints
 */
export const invoicingEndpoints = {
    /**
     * Verify VAT number via VIES
     */
    async verifyVIES(input) {
        const response = await getApiClient().post("/invoice/vies/verify", input);
        return parseStrapiItemResponse(response.data, {
            schema: ViesVerificationResultSchema,
            context: "invoicingEndpoints.verifyVIES",
        });
    },
    /**
     * Validate tax profile
     */
    async validateTaxProfile(input) {
        const response = await getApiClient().post("/invoice/tax-profile/validate", input);
        return parseStrapiItemResponse(response.data, {
            schema: TaxProfileValidationResultSchema,
            context: "invoicingEndpoints.validateTaxProfile",
        });
    },
    /**
     * Classify referrer for tax purposes
     */
    async classifyReferrer(input) {
        const response = await getApiClient().post("/invoice/referrer/classify", input);
        return parseStrapiItemResponse(response.data, {
            schema: ReferrerClassificationSchema,
            context: "invoicingEndpoints.classifyReferrer",
        });
    },
    /**
     * Check payment eligibility
     */
    async checkPaymentEligibility(input) {
        const response = await getApiClient().post("/invoice/payment/check-eligibility", input);
        return parseStrapiItemResponse(response.data, {
            schema: PaymentEligibilityResultSchema,
            context: "invoicingEndpoints.checkPaymentEligibility",
        });
    },
    /**
     * Check claim eligibility
     */
    async checkClaimEligibility() {
        const response = await getApiClient().get("/invoice/claim/eligibility");
        return parseStrapiItemResponse(response.data, {
            schema: ClaimEligibilitySchema,
            context: "invoicingEndpoints.checkClaimEligibility",
        });
    },
    /**
     * Get claim preview
     */
    async getClaimPreview() {
        const response = await getApiClient().post("/invoice/claim/preview");
        return parseStrapiItemResponse(response.data, {
            schema: ClaimPreviewSchema,
            context: "invoicingEndpoints.getClaimPreview",
        });
    },
    /**
     * Confirm a claim
     */
    async confirmClaim(input) {
        const response = await getApiClient().post("/invoice/claim/confirm", input);
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Get claim history
     * Returns merged data from old payouts and new invoices
     */
    async getClaimHistory() {
        const response = await getApiClient().get("/invoice/claim/history");
        return parseStrapiItemResponse(response.data, {
            schema: ClaimHistoryResponseSchema,
            context: "invoicingEndpoints.getClaimHistory",
        });
    },
    /**
     * Get current user's invoices
     */
    async getMyInvoices(params) {
        const response = await getApiClient().get("/invoice/me", { params });
        return parseStrapiListResponse(response.data, {
            schema: InvoiceSchema,
            context: "invoicingEndpoints.getMyInvoices",
        });
    },
    /**
     * Download invoice PDF
     */
    async downloadMyInvoicePDF(invoiceId) {
        return getApiClient().getBlob(`/invoice/${invoiceId}/pdf`);
    },
    /**
     * Cancel a pending claim
     */
    async cancelClaim(invoiceId) {
        const response = await getApiClient().post(`/invoice/${invoiceId}/cancel-claim`);
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Get invoice claim link
     */
    async getInvoiceClaimLink(invoiceId) {
        const response = await getApiClient().get(`/invoice/${invoiceId}/claim-link`);
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Open invoice PDF in new tab (returns URL)
     */
    async openMyInvoicePDF(invoiceId) {
        const response = await getApiClient().get(`/invoice/${invoiceId}/pdf-url`);
        return parseStrapiItemResponse(response.data);
    },
    // Admin endpoints
    /**
     * List referrers (admin)
     */
    async adminListReferrers(params) {
        const response = await getApiClient().get("/invoice/admin/referrers", { params });
        // No schema - custom response type
        return parseStrapiListResponse(response.data);
    },
    /**
     * Preview invoice (admin) - returns PDF blob
     */
    async adminPreviewInvoice(input) {
        return getApiClient().postBlob("/invoice/admin/preview", input);
    },
    /**
     * Generate test invoice (admin)
     */
    async adminGenerateTestInvoice(input) {
        const response = await getApiClient().post("/invoice/admin/generate", input);
        return parseStrapiItemResponse(response.data, {
            schema: InvoiceSchema,
            context: "invoicingEndpoints.adminGenerateTestInvoice",
        });
    },
    /**
     * Download invoice PDF (admin)
     */
    async adminDownloadInvoicePDF(invoiceId) {
        return getApiClient().getBlob(`/invoice/admin/${invoiceId}/pdf`);
    },
    /**
     * List all invoices (admin)
     */
    async adminListInvoices(params) {
        const response = await getApiClient().get("/invoice/admin/invoices", { params });
        return parseStrapiListResponse(response.data, {
            schema: InvoiceSchema,
            context: "invoicingEndpoints.adminListInvoices",
        });
    },
    /**
     * Delete test invoice (admin)
     */
    async adminDeleteTestInvoice(invoiceId) {
        const response = await getApiClient().delete(`/invoice/admin/${invoiceId}`);
        return parseStrapiItemResponse(response.data);
    },
};
/**
 * Tax report endpoints
 */
export const taxReportsEndpoints = {
    /**
     * Get available report types
     */
    async getTypes() {
        const response = await getApiClient().get("/tax-reports/types");
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Get reports for a year
     */
    async getReportsByYear(year) {
        const response = await getApiClient().get(`/tax-reports/year/${year}`);
        return parseStrapiListResponse(response.data, {
            schema: TaxReportSchema,
            context: "taxReportsEndpoints.getReportsByYear",
        });
    },
    /**
     * Get all reports
     */
    async getReports(params) {
        const response = await getApiClient().get("/tax-reports", {
            params,
        });
        return parseStrapiListResponse(response.data, {
            schema: TaxReportSchema,
            context: "taxReportsEndpoints.getReports",
        });
    },
    /**
     * Get report by ID
     */
    async getReport(id) {
        const response = await getApiClient().get(`/tax-reports/${id}`);
        return parseStrapiItemResponse(response.data, {
            schema: TaxReportSchema,
            context: "taxReportsEndpoints.getReport",
        });
    },
    /**
     * Generate a tax report
     */
    async generate(input) {
        const response = await getApiClient().post("/tax-reports/generate", input);
        return parseStrapiItemResponse(response.data, {
            schema: TaxReportSchema,
            context: "taxReportsEndpoints.generate",
        });
    },
    /**
     * Export report as CSV
     */
    async exportCSV(id) {
        return getApiClient().getBlob(`/tax-reports/${id}/export/csv`);
    },
    /**
     * Delete a report
     */
    async deleteReport(id) {
        const response = await getApiClient().delete(`/tax-reports/${id}`);
        return parseStrapiItemResponse(response.data);
    },
};
/**
 * Manual payment endpoints (admin)
 */
export const manualPaymentsEndpoints = {
    /**
     * Get pending invoices
     */
    async getPendingInvoices(params) {
        const response = await getApiClient().get("/invoice/admin/manual-pending", { params });
        return parseStrapiListResponse(response.data, {
            schema: InvoiceSchema,
            context: "manualPaymentsEndpoints.getPendingInvoices",
        });
    },
    /**
     * Record a manual payment
     */
    async recordPayment(invoiceId, input) {
        const response = await getApiClient().post(`/invoice/admin/${invoiceId}/record-payment`, input);
        return parseStrapiItemResponse(response.data, {
            schema: InvoiceSchema,
            context: "manualPaymentsEndpoints.recordPayment",
        });
    },
    /**
     * Get payment history
     */
    async getPaymentHistory(params) {
        const response = await getApiClient().get("/invoice/admin/manual-history", { params });
        return parseStrapiListResponse(response.data, {
            schema: ManualPaymentHistoryItemSchema,
            context: "manualPaymentsEndpoints.getPaymentHistory",
        });
    },
    /**
     * Download invoice PDF
     */
    async downloadInvoicePDF(invoiceId) {
        return getApiClient().getBlob(`/invoice/admin/${invoiceId}/pdf`);
    },
};
/**
 * Billing data endpoints
 */
export const billingDataEndpoints = {
    /**
     * Get current user's billing data
     */
    async getMyBillingData() {
        const response = await getApiClient().get("/billing-data/me");
        return parseStrapiItemResponse(response.data, {
            schema: BillingDataSchema,
            context: "billingDataEndpoints.getMyBillingData",
        });
    },
    /**
     * Update current user's billing data
     */
    async updateMyBillingData(input) {
        const response = await getApiClient().put("/billing-data/me", { data: input });
        return parseStrapiItemResponse(response.data, {
            schema: BillingDataSchema,
            context: "billingDataEndpoints.updateMyBillingData",
        });
    },
    /**
     * Get current user's issuer billing data
     */
    async getMyIssuerBillingData() {
        const response = await getApiClient().get("/issuer-billing-data/me");
        return parseStrapiItemResponse(response.data, {
            schema: BillingDataSchema,
            context: "billingDataEndpoints.getMyIssuerBillingData",
        });
    },
    /**
     * Update current user's issuer billing data
     */
    async updateMyIssuerBillingData(input) {
        const response = await getApiClient().put("/issuer-billing-data/me", { data: input });
        return parseStrapiItemResponse(response.data, {
            schema: BillingDataSchema,
            context: "billingDataEndpoints.updateMyIssuerBillingData",
        });
    },
};
/**
 * Payment method endpoints
 */
export const paymentMethodsEndpoints = {
    /**
     * Get all payment methods
     */
    async getPaymentMethods(params) {
        const populate = PAYMENT_METHOD_POPULATE.DEFAULT;
        const response = await getApiClient().get("/payment-methods", {
            params: {
                populate,
                ...params,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: PaymentMethodSchema,
            populate,
            context: "paymentMethodsEndpoints.getPaymentMethods",
        });
    },
    /**
     * Remove a payment method
     */
    async removePaymentMethod(id) {
        const response = await getApiClient().delete(`/payment-methods/${id}`);
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Get Revolut payment method token
     */
    async getRevolutPaymentMethodToken() {
        const response = await getApiClient().get("/payment-methods/revolut/token");
        return parseStrapiItemResponse(response.data, {
            schema: RevolutPaymentMethodTokenSchema,
            context: "paymentMethodsEndpoints.getRevolutPaymentMethodToken",
        });
    },
    /**
     * Confirm Revolut payment method creation
     */
    async confirmRevolutPaymentMethodCreation(orderId) {
        const response = await getApiClient().post(`/payment-methods/revolut/${orderId}`);
        return parseStrapiItemResponse(response.data, {
            schema: RevolutPaymentConfirmationSchema,
            context: "paymentMethodsEndpoints.confirmRevolutPaymentMethodCreation",
        });
    },
};
/**
 * Crypto payment config endpoints
 */
export const cryptoPaymentConfigEndpoints = {
    /**
     * Get crypto payment config (admin)
     */
    async get() {
        const response = await getApiClient().get("/crypto-payment-config");
        return parseStrapiItemResponse(response.data, {
            schema: CryptoPaymentConfigSchema,
            context: "cryptoPaymentConfigEndpoints.get",
        });
    },
    /**
     * Update crypto payment config (admin)
     */
    async update(input) {
        const response = await getApiClient().put("/crypto-payment-config", { data: input });
        return parseStrapiItemResponse(response.data, {
            schema: CryptoPaymentConfigSchema,
            context: "cryptoPaymentConfigEndpoints.update",
        });
    },
    /**
     * Get crypto payment options (public)
     */
    async getOptions() {
        const response = await getApiClient().get("/crypto-payment-config/options");
        return parseStrapiItemResponse(response.data, {
            schema: CryptoPaymentOptionsSchema,
            context: "cryptoPaymentConfigEndpoints.getOptions",
        });
    },
};
